import type { Company, ReferentMutation } from '../@types/Company';
import { getCompanyDisplayName } from './company';
import { buildSciUnsubscribeUrl } from './newsletterMail';
import { formatPhoneNumber } from './newsletterPreview';

/**
 * Replace the `[data-company-name]` and `[data-referent-*]` placeholders used
 * in Référent Mutation Location email templates with values from the provided
 * company and referent. When `referent` is null, the function falls back to
 * the first valid referent on the company's branding settings (matching the
 * historical behaviour of the {@link Variable} component).
 *
 * Used both by:
 *  - the on-screen `<Variable>` component (renders for the default referent)
 *  - the PDF utility that generates one document per referent (each invocation
 *    substitutes for a different referent so the produced PDFs carry the right
 *    contact details)
 */
export const substituteReferentVariables = (
  content: string,
  company: Company | null,
  referent?: ReferentMutation | null,
): string => {
  if (!company) {
    return content;
  }

  let processed = content;

  const displayName = getCompanyDisplayName(company);
  if (displayName) {
    processed = processed.replaceAll('[data-company-name]', displayName);
  }

  const targetReferent: ReferentMutation | null = referent
    ?? (company.brandingSettings?.referents && company.brandingSettings.referents.length > 0
      ? company.brandingSettings.referents[0]
      : null);

  if (targetReferent) {
    if (targetReferent.title) {
      processed = processed.replaceAll('[data-referent-title]', targetReferent.title);
    }

    if (targetReferent.firstname) {
      processed = processed.replaceAll('[data-referent-firstname]', targetReferent.firstname);
    }

    if (targetReferent.lastname) {
      processed = processed.replaceAll('[data-referent-lastname]', targetReferent.lastname);
    }

    processed = processed.replaceAll('[data-referent-email]', targetReferent.email || '');
    const formattedPhone = targetReferent.phone ? formatPhoneNumber(targetReferent.phone) : '';
    processed = processed.replaceAll('[data-referent-phone]', formattedPhone);

    const contactParts: string[] = [];
    if (targetReferent.email) { contactParts.push(targetReferent.email); }
    if (targetReferent.phone) { contactParts.push(formattedPhone); }
    processed = processed.replaceAll('[CONTACT_SOUSTRAITANT_MAIL]', contactParts.join(' - '));
  } else {
    processed = processed.replaceAll('[data-referent-email]', '');
    processed = processed.replaceAll('[data-referent-phone]', '');
    processed = processed.replaceAll('[CONTACT_SOUSTRAITANT_MAIL]', '');
  }

  if (displayName) {
    processed = processed.replaceAll('[NOM_SOUSTRAITANT_MAIL]', displayName);
  } else {
    processed = processed.replaceAll('[NOM_SOUSTRAITANT_MAIL]', '');
  }

  if (company.details) {
    const addressParts = [
      company.details.address,
      company.details.postalCode,
      company.details.city,
    ].filter(Boolean).join(', ');
    processed = processed.replaceAll('[ADRESSE_SOUSTRAITANT_MAIL]', addressParts);
    processed = processed.replaceAll('[REPRESENTANT_LEGAL_SOUSTRAITANT_MAIL]', company.details.infoDirigeant || '');
  } else {
    processed = processed.replaceAll('[ADRESSE_SOUSTRAITANT_MAIL]', '');
    processed = processed.replaceAll('[REPRESENTANT_LEGAL_SOUSTRAITANT_MAIL]', '');
  }

  const sciUnsubscribeUrl = buildSciUnsubscribeUrl(company);
  processed = processed.replaceAll('[LIEN_DE_DESINSCRIPTION]', sciUnsubscribeUrl);

  return processed;
};
